import React, { useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import DataForm from 'forms/DataForm';
import { request } from 'utilities/graph';
import { updateFacilitySettings, createFacilitySettings, updateClient } from 'graphql/mutations';
import ImageGalleryUploader from 'components/ImageGalleryUploader';
import { useCache } from 'CacheProvider';
import moment from 'moment-timezone';
import cache from 'utilities/cache';
import uiSchema from './uiSchema';
import { getCustomizeDisadvantagedSettingsSchema } from 'forms/schemas';

export default function FacilitySettingsForm({ formData: inFormData, ...props }) {
  // load here for translation purpose
  const { default: schema } = useMemo(() => require('./schema.js'), []);
  const [currentImageLogoS3Keys, setCurrentImageLogoS3Keys] = useState([]);
  const [currentImageCompanyStampS3Keys, setCurrentImageCompanyStampS3Keys] = useState([]);
  const [currentImageChairmanStampS3Keys, setCurrentImageChairmanStampS3Keys] = useState([]);
  const [currentImageAccountantStampS3Keys, setCurrentImageAccountantStampS3Keys] = useState([]);
  const [currentImageHandlerStampS3Keys, setCurrentImageHandlerStampS3Keys] = useState([]);
  const [newImageLogoS3Keys, setNewImageLogoS3Keys] = useState([]);
  const [newImageCompanyStampS3Keys, setNewImageCompanyStampS3Keys] = useState([]);
  const [newImageChairmanStampS3Keys, setNewImageChairmanStampS3Keys] = useState([]);
  const [newImageAccountantStampS3Keys, setNewImageAccountantStampS3Keys] = useState([]);
  const [newImageHandlerStampS3Keys, setNewImageHandlerStampS3Keys] = useState([]);
  const { appGroup, setLogoS3Keys, selectedUserGroupParams, setSelectedUserGroupParams } = useCache();
  const [formData, setFormData] = useState();
  const facilityMode = appGroup === 'FacilityAdmins';

  useEffect(() => {
    if (inFormData?.facilitySettings?.logoS3Keys && inFormData.facilitySettings.logoS3Keys[0]) {
      setCurrentImageLogoS3Keys(inFormData.facilitySettings.logoS3Keys);
      setNewImageLogoS3Keys(inFormData.facilitySettings.logoS3Keys);
    }
    if (inFormData?.facilitySettings?.companyStampS3Keys && inFormData.facilitySettings.companyStampS3Keys[0]) {
      setCurrentImageCompanyStampS3Keys(inFormData.facilitySettings.companyStampS3Keys);
      setNewImageCompanyStampS3Keys(inFormData.facilitySettings.companyStampS3Keys);
    }
    if (inFormData?.facilitySettings?.chairmanStampS3Keys && inFormData.facilitySettings.chairmanStampS3Keys[0]) {
      setCurrentImageChairmanStampS3Keys(inFormData.facilitySettings.chairmanStampS3Keys);
      setNewImageChairmanStampS3Keys(inFormData.facilitySettings.chairmanStampS3Keys);
    }
    if (inFormData?.facilitySettings?.accountantStampS3Keys && inFormData.facilitySettings.accountantStampS3Keys[0]) {
      setCurrentImageAccountantStampS3Keys(inFormData.facilitySettings.accountantStampS3Keys);
      setNewImageAccountantStampS3Keys(inFormData.facilitySettings.accountantStampS3Keys);
    }
    if (inFormData?.facilitySettings?.handlerStampS3Keys && inFormData.facilitySettings.handlerStampS3Keys[0]) {
      setCurrentImageHandlerStampS3Keys(inFormData.facilitySettings.handlerStampS3Keys);
      setNewImageHandlerStampS3Keys(inFormData.facilitySettings.handlerStampS3Keys);
    }
    if (inFormData) {
      if (inFormData.facilitySettings) {
        setFormData(inFormData);
      } else {
        setFormData({
          ...inFormData,
          facilitySettings: { // 避免rjsf跳出格式檢查錯誤
            lunchStartTime: null,
            lunchEndTime: null,
            dinnerStartTime: null,
            dinnerEndTime: null,
            deliverySetting: null,
            receiptInfo: null,
          },
        });
      }
    }
  }, [inFormData]);

  const updateFunc = async (data) => {
    data.facilitySettings.logoS3Keys = newImageLogoS3Keys;
    data.facilitySettings.companyStampS3Keys = newImageCompanyStampS3Keys;
    data.facilitySettings.chairmanStampS3Keys = newImageChairmanStampS3Keys;
    data.facilitySettings.accountantStampS3Keys = newImageAccountantStampS3Keys;
    data.facilitySettings.handlerStampS3Keys = newImageHandlerStampS3Keys;
    if (facilityMode) {
      setLogoS3Keys([...newImageLogoS3Keys]);
    }
    if (data.facilitySettings.id) {
      await request(updateFacilitySettings, { input: data.facilitySettings });
    } else {
      const now = moment().toISOString();
      const userName = cache.get('app:userName');
      Object.assign(data.facilitySettings, {
        createdAt: now,
        createdBy: userName,
        updatedAt: now,
        updatedBy: userName,
      });
      const { data: { createFacilitySettings: { id: facilitySettingsId } } } =
        await request(createFacilitySettings, { input: data.facilitySettings });
      data.facilitySettings.id = facilitySettingsId;
      await request(updateClient, { input: {
        id: data.id,
        type: data.type,
        isActive: data.isActive,
        county: data.county,
        name: data.name,
        address: data.address,
        email: data.email,
        usersLimit: data.usersLimit,
        eldersLimit: data.eldersLimit,
        createdAt: data.createdAt,
        createdBy: data.createdBy,
        updatedAt: now,
        updatedBy: userName,
        facilitySettingsId: facilitySettingsId,
      } });
    }
    if (facilityMode) {
      selectedUserGroupParams.clientData.facilitySettings = data.facilitySettings;
      setSelectedUserGroupParams(selectedUserGroupParams);
    }
  };

  if (!formData) {
    return <></>;
  }

  return (
    <DataForm
      schema={schema}
      uiSchema={uiSchema}
      createFunc={() => {}}
      updateFunc={updateFunc}
      formData={formData}
      dirty={true}
      extMappings={[{
        key: 'disadvantagedType',
        func: () => {
          let clientId;
          if (appGroup === 'Admins') {
            clientId = inFormData.id;
          } else {
            clientId = cache.get('app:facilityId');
          }
          return getCustomizeDisadvantagedSettingsSchema(clientId, false);
        },
      }]}
      {...props}
    >
      <Typography variant="h5">
        商標圖示
      </Typography>
      <Typography variant="body2" color='textSecondary'>
        顯示在左側功能清單上方
      </Typography>
      <Divider />
      <ImageGalleryUploader
        s3Prefix={`facilitySettings/${inFormData.id}/logo`}
        s3Keys={currentImageLogoS3Keys}
        onUpdateS3Keys={setNewImageLogoS3Keys}
      />
      <Typography variant="h5" style={{ marginTop: 10 }}>
        收據公司章
      </Typography>
      <Typography variant="body2" color='textSecondary'>
        顯示在收據，建議尺寸 150 x 150
      </Typography>
      <Divider />
      <ImageGalleryUploader
        s3Prefix={`facilitySettings/${inFormData.id}/companyStamp`}
        s3Keys={currentImageCompanyStampS3Keys}
        onUpdateS3Keys={setNewImageCompanyStampS3Keys}
      />
      <Typography variant="h5" style={{ marginTop: 10 }}>
        收據主管章
      </Typography>
      <Typography variant="body2" color='textSecondary'>
        顯示在收據，建議尺寸 50 x 50
      </Typography>
      <Divider />
      <ImageGalleryUploader
        s3Prefix={`facilitySettings/${inFormData.id}/chairmanStamp`}
        s3Keys={currentImageChairmanStampS3Keys}
        onUpdateS3Keys={setNewImageChairmanStampS3Keys}
      />
      <Typography variant="h5" style={{ marginTop: 10 }}>
        收據會計章
      </Typography>
      <Typography variant="body2" color='textSecondary'>
        顯示在收據，建議尺寸 50 x 50
      </Typography>
      <Divider />
      <ImageGalleryUploader
        s3Prefix={`facilitySettings/${inFormData.id}/accountantStamp`}
        s3Keys={currentImageAccountantStampS3Keys}
        onUpdateS3Keys={setNewImageAccountantStampS3Keys}
      />
      <Typography variant="h5" style={{ marginTop: 10 }}>
        收據經手人章
      </Typography>
      <Typography variant="body2" color='textSecondary'>
        顯示在收據，建議尺寸 50 x 50
      </Typography>
      <Divider />
      <ImageGalleryUploader
        s3Prefix={`facilitySettings/${inFormData.id}/handlerStamp`}
        s3Keys={currentImageHandlerStampS3Keys}
        onUpdateS3Keys={setNewImageHandlerStampS3Keys}
      />
    </DataForm>
  );
}

FacilitySettingsForm.propTypes = {
  data: PropTypes.object,
  formData: PropTypes.object,
  onComplete: PropTypes.func,
};
